import {tx} from '@cityofzion/neon-core'
import {
  Arg,
  ArrayArgType,
  ContractInvocation,
} from '@cityofzion/neon-dappkit-types'
import {$} from '@/facade'
import {EnvHelper} from '@/helpers/EnvHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'
import {NeoHelper} from '@/helpers/NeoHelper'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {
  AddOrbitProjectArgs,
  ClaimOrbitArgs,
  DistributeOrbitRewardArgs,
  DepositTokenArgs,
  EditOrbitProjectArgs,
  FundOrbitProjectArgs,
  InvocationBuilderResult,
  N3InvocationBuilder,
  OrbitOwnerArgs,
  RefundOrbitContractArgs,
  RemoveOrbitProjectArgs,
  SetClaimableNEP17Args,
  SetupOrbitArgs,
  StandardConvertArgs,
  StartOrbitRoundArgs,
  SwapNeoUsingNeoProxyArgs,
} from '@/model/wallets/invocationsBuilder/N3InvocationBuilder'
import {ContractInvocationMulti} from '@/libs/wallet-connect-sdk-core/types'

class N3NeonWalletInvocationBuilder implements N3InvocationBuilder {
  private connectedSignerAccount() {
    return NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
  }

  claimGasInvocation(address: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress(address)
    const to = NeoHelper.getScriptHashFromAddress(address)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          operation: 'transfer',
          args: [
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: '0',
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  voteInvocation(candidatePublicKey: string | null): InvocationBuilderResult {
    const publicKeyArg: Arg = candidatePublicKey
      ? {
          type: 'PublicKey',
          value: `0${candidatePublicKey}`,
        }
      : {
          type: 'Any',
          value: null,
        }

    const invocationBuilderResult: ContractInvocationMulti = {
      invocations: [
        {
          operation: 'vote',
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          args: [
            {
              type: 'Hash160',
              value: $.walletAdapter.n3Address!,
            },
            publicKeyArg,
          ],
        },
      ],
      signers: [
        {
          account: this.connectedSignerAccount(),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return invocationBuilderResult
  }

  swapNeoUsingNeoProxyInvocation({
    address,
    neoAmountIn,
    gasAmountOutMin,
  }: SwapNeoUsingNeoProxyArgs): InvocationBuilderResult {
    return {
      invocations: [
        {
          scriptHash: EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
          operation: 'swapNeo',
          args: [
            {
              type: 'Hash160',
              value: address,
            },
            {
              type: 'Integer',
              value: neoAmountIn,
            },
            {
              type: 'Integer',
              value: gasAmountOutMin,
            },
          ],
        },
      ],
      networkFeeOverride: 10000000,
      signers: [
        {
          account: EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
          scopes: tx.WitnessScope.Global,
        },
        {
          account: NeoHelper.getScriptHashFromAddress(address),
          scopes: tx.WitnessScope.Global,
          allowedContracts: [
            EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
            NeoHelper.getScriptHashFromAddress(address),
          ],
        },
      ],
    }
  }

  standardConvertInvocation({
    path,
    pairIds,
    amountToUse,
    minAmountOut,
  }: StandardConvertArgs): InvocationBuilderResult {
    const tokenToUse = path[0]
    const tokenToReceive = path[path.length - 1]

    const amountToUseFormatted = Number(amountToUse) * 10 ** tokenToUse.decimals

    const minimumReceivedFormatted = BigInt(
      (Number(minAmountOut) * 10 ** tokenToReceive.decimals).toFixed(0)
    )

    const contractInvocationMulti = {
      invocations: [
        {
          scriptHash: '0xf40f694362957d56801a8cef7e62a83f7f1b7b0f',
          operation: 'standardConvert',
          args: [
            {
              type: 'Hash160',
              value: NeoHelper.getScriptHashFromAddress(
                $.walletAdapter.n3Address!
              ),
            },
            {
              type: 'Integer',
              value: String(amountToUseFormatted),
            },
            {
              type: 'Integer',
              value: String(minimumReceivedFormatted),
            },
            {
              type: 'Array',
              value: path.map(flamingoToken => {
                return {
                  type: 'Hash160',
                  value: flamingoToken.hash,
                }
              }),
            },
            {
              type: 'Array',
              value: pairIds.map(id => {
                return {
                  type: 'Integer',
                  value: id,
                }
              }),
            },
          ],
        },
      ],
      signers: [
        {
          account: this.connectedSignerAccount(),
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            '0xec268e9c642b7d09d10fe658bcb1cc63c0895d4d',
            '0xf40f694362957d56801a8cef7e62a83f7f1b7b0f',
            ...path.map(flamingoToken => flamingoToken.hash),
          ],
        },
      ],
    }

    return contractInvocationMulti as any
  }

  depositNativeInvocation(amount: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = $.walletAdapter.xAddress!
    const amountToUseFormatted = (Number(amount) * 10 ** 8).toFixed()
    const maxFee = '10000000'

    const invocations = {
      invocations: [
        {
          scriptHash: BridgeNetworkHelper.n3BridgeScriptHash(),
          fee: '0.0001',
          operation: 'depositNative',
          args: [
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amountToUseFormatted,
            },
            {
              type: 'Integer',
              value: maxFee,
            },
          ],
        },
      ],
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            BridgeNetworkHelper.n3BridgeScriptHash(),
            EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
            EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          ],
        },
      ],
      extraSystemFee: 0,
      extraNetworkFee: 0,
    } as any

    return invocations
  }

  depositTokenInvocation({
    amount,
    tokenDecimals,
    tokenHash,
  }: DepositTokenArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = $.walletAdapter.xAddress!
    const amountToUseFormatted = (
      Number(amount) *
      10 ** tokenDecimals
    ).toFixed()
    const maxFee = '10000000'

    const invocations = {
      invocations: [
        {
          scriptHash: BridgeNetworkHelper.n3BridgeScriptHash(),
          fee: '0.0001',
          operation: 'depositToken',
          args: [
            {
              type: 'Hash160',
              value: tokenHash,
            },
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amountToUseFormatted,
            },
            {
              type: 'Integer',
              value: maxFee,
            },
          ],
        },
      ],
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            BridgeNetworkHelper.n3BridgeScriptHash(),
            EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
            EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
            tokenHash,
          ],
        },
      ],
    } as any

    return invocations
  }

  wrapNEOInvocation(amount: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = EnvHelper.VUE_APP_BNEO_SCRIPT_HASH

    const invocations = {
      invocations: [
        {
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          operation: 'transfer',
          args: [
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    } as any

    return invocations
  }

  unwrapBNEOInvocation(amount: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = EnvHelper.VUE_APP_BNEO_SCRIPT_HASH
    const amountFormatted = (Number(amount) * 10 ** 5).toFixed()

    const invocations = {
      invocations: [
        {
          scriptHash: EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
          operation: 'transfer',
          args: [
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amountFormatted,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    } as any

    return invocations
  }

  addOrbitProjectInvocation({
    projectScriptHash,
    tokenScriptHash,
    amount,
    assetType,
    eventName,
  }: AddOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'addProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Hash160',
              value: tokenScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Integer',
              value: assetType,
            },
            {
              type: 'String',
              value: eventName,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  claimOrbitInvocation({
    projectScriptHash,
    instanceId,
  }: ClaimOrbitArgs): InvocationBuilderResult {
    const signerAccount = this.connectedSignerAccount()

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'claim',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
          ],
        },
      ],
      signers: [
        {
          account: signerAccount,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  distributeRewardInvocation({
    roundNumber,
  }: DistributeOrbitRewardArgs): InvocationBuilderResult {
    const signerAccount = this.connectedSignerAccount()

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'distributeReward',
          args: [
            {
              type: 'Integer',
              value: roundNumber,
            },
          ],
        },
      ],
      signers: [
        {
          account: signerAccount,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  setClaimableNEP17Invocation({
    userScriptHash,
    projectScriptHash,
    amount,
  }: SetClaimableNEP17Args): InvocationBuilderResult {
    const signerAccount = this.connectedSignerAccount()

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'setClaimableNEP17',
          args: [
            {
              type: 'Hash160',
              value: userScriptHash,
            },
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
          ],
        },
      ],
      signers: [
        {
          account: signerAccount,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  editOrbitProjectInvocation({
    projectScriptHash,
    tokenScriptHash,
    amount,
    assetType,
    instanceId,
    eventName,
  }: EditOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'editProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
            {
              type: 'Hash160',
              value: tokenScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Integer',
              value: assetType,
            },
            {
              type: 'String',
              value: eventName,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  endOrbitRoundInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'endRound',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  forceEndOrbitRoundInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'forceEndRound',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  setClaimableNEP17MultipleInvocation(
    data: SetClaimableNEP17Args[]
  ): InvocationBuilderResult {
    const contractInvocations: ContractInvocation[] = data.map(
      ({userScriptHash, projectScriptHash, amount}) => ({
        scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
        operation: 'setClaimableNEP17',
        args: [
          {type: 'Hash160', value: userScriptHash},
          {type: 'Hash160', value: projectScriptHash},
          {type: 'Integer', value: amount},
        ],
      })
    )

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: contractInvocations,
      signers: [
        {
          account: this.connectedSignerAccount(),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  fundOrbitProjectsInvocation(
    data: FundOrbitProjectArgs[]
  ): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocations: ContractInvocation[] = data.map(item => {
      return {
        scriptHash: item.tokenScriptHash,
        operation: 'transfer',
        args: [
          {
            type: 'Hash160',
            value: from,
          },
          {
            type: 'Hash160',
            value: EnvHelper.orbitScriptHash(currentN3Network()),
          },
          {
            type: 'Integer',
            value: item.amount,
          },
          {
            type: 'Array',
            value: [
              {
                type: 'Hash160',
                value: item.projectScriptHash,
              },
              {
                type: 'Integer',
                value: item.instanceId,
              },
            ],
          },
        ],
      }
    })

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: contractInvocations,
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  pauseOrbitInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'pause',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  removeOrbitProjectInvocation({
    projectScriptHash,
    instanceId,
  }: RemoveOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'removeProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  setupOrbitInvocation({
    roundDuration,
    claimDuration,
    distributionDuration,
    lockoutDuration,
  }: SetupOrbitArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'setupOrbit',
          args: [
            {type: 'Integer', value: roundDuration},
            {type: 'Integer', value: claimDuration},
            {type: 'Integer', value: distributionDuration},
            {type: 'Integer', value: lockoutDuration},
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  startOrbitRoundInvocation({
    projectInstances,
  }: StartOrbitRoundArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const argValue: ArrayArgType[] = projectInstances.map(projectInstance => {
      return {
        type: 'Array',
        value: [
          {
            type: 'Hash160',
            value: projectInstance.scriptHash,
          },
          {
            type: 'Integer',
            value: projectInstance.instanceId,
          },
        ],
      }
    })

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'startRound',
          args: [
            {
              type: 'Array',
              value: argValue,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  unpauseOrbitInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'unpause',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  addOrbitOwnerInvocation({
    ownerAddress,
  }: OrbitOwnerArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'addOwner',
          args: [
            {
              type: 'Hash160',
              value: ownerAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  removeOrbitOwnerInvocation({
    ownerAddress,
  }: OrbitOwnerArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'removeOwner',
          args: [
            {
              type: 'Hash160',
              value: ownerAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }

  refundOrbitContractInvocation({
    recipientAddress,
  }: RefundOrbitContractArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocationMulti: ContractInvocationMulti = {
      invocations: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'refundContract',
          args: [
            {
              type: 'Hash160',
              value: recipientAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }

    return contractInvocationMulti
  }
}

export const n3NeonWalletInvocationBuilder = new N3NeonWalletInvocationBuilder()
